import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Send, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { INGESTION_API } from '@/api/client'
import type { ApiCredentials } from '../types/ingestion.types'
import { useAccount } from '@/contexts/AccountContext'
import { listEventMappings } from '@/features/derivation-config/api/derivationConfig'
import type { EventMapping } from '@/features/derivation-config/types'
import type { CustomEventResponse } from '../types/ingestion.types'

interface EventsBrowserProps {
  initialMapping?: EventMapping
}

export function EventsBrowser({ initialMapping }: EventsBrowserProps) {
  const { selectedAccountId } = useAccount()
  const accId = selectedAccountId && selectedAccountId !== 'ALL'
    ? parseInt(selectedAccountId, 10)
    : undefined

  // Client-side filters
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  // Selected event and form
  const [selectedMapping, setSelectedMapping] = useState<EventMapping | null>(initialMapping ?? null)
  const [uniFields, setUniFields] = useState<Array<{ key: string; value: string }>>([
    { key: 'email', value: '' },
  ])
  const [customFields, setCustomFields] = useState<Record<string, string>>({})
  const [fhid, setFhid] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [response, setResponse] = useState<CustomEventResponse | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // API key credentials (same as SingleEventTester — stored in localStorage)
  const getCredentials = (): ApiCredentials | null => {
    try {
      const stored = localStorage.getItem('fh_api_credentials')
      return stored ? JSON.parse(stored) : null
    } catch { return null }
  }
  const credentials = getCredentials()

  // Pre-populate fields when arriving with an initial mapping
  useEffect(() => {
    if (initialMapping) {
      const fields: Record<string, string> = {}
      Object.keys(initialMapping.mappings).forEach(k => { fields[k] = '' })
      setCustomFields(fields)
    }
  }, [initialMapping])

  // ── Load event mappings (same source as derivation-config) ─────────────────
  const { data: allMappings = [], isLoading, error, refetch } = useQuery({
    queryKey: ['event-mappings-browser', accId],
    queryFn: () => listEventMappings(accId),
    enabled: false, // manual trigger
  })

  // Client-side filter
  const mappings = allMappings.filter(m => {
    const matchesSearch = !searchQuery ||
      m.eventName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.category.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !selectedCategory || m.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const categories = [...new Set(allMappings.map(m => m.category))]

  // ── Select event ───────────────────────────────────────────────────────────
  const selectMapping = (mapping: EventMapping) => {
    setSubmitError(null)
    setSelectedMapping(mapping)
    // Use mapping keys as expected form fields (intermediate field names)
    const fields: Record<string, string> = {}
    Object.keys(mapping.mappings).forEach(k => { fields[k] = '' })
    setCustomFields(fields)
    setUniFields([{ key: 'email', value: '' }])
    setFhid('')
    setResponse(null)
    setTimeout(() => {
      document.getElementById('event-form')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  // ── Submit event ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedMapping) return
    const creds = getCredentials()
    if (!creds) {
      setSubmitError('API credentials not configured. Go to the Single Event API tab to set them.')
      return
    }
    try {
      setIsSubmitting(true)
      setSubmitError(null)
      setResponse(null)

      const uni: Record<string, string> = {}
      uniFields.forEach(f => { if (f.key && f.value) uni[f.key] = f.value })
      if (Object.keys(uni).length === 0) throw new Error('At least one uni identifier is required')

      const custom: Record<string, string> = {}
      Object.entries(customFields).forEach(([k, v]) => { if (v) custom[k] = v })

      const requestData: Record<string, unknown> = {
        event_type: selectedMapping.eventType,
        event_name: selectedMapping.eventName,
        category: selectedMapping.category,
        uni,
        custom,
      }
      if (fhid) requestData.fhid = fhid
      if (accId) requestData.acc_id = accId

      const res = await fetch(`${INGESTION_API}/events/custom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-FH-Key-ID': creds.keyId,
          'X-FH-Key-Secret': creds.secret,
        },
        body: JSON.stringify(requestData),
      })
      const data: CustomEventResponse = await res.json()
      if (!res.ok) throw new Error((data as any).message || `HTTP ${res.status}`)
      setResponse(data)

      const historyItem = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        method: 'POST' as const,
        endpoint: '/api/v1/events/custom',
        status: 200,
        duration: 0,
        request: requestData,
        response: data,
      }
      const history = JSON.parse(localStorage.getItem('fh_request_history') || '[]')
      history.unshift(historyItem)
      localStorage.setItem('fh_request_history', JSON.stringify(history.slice(0, 50)))
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  const addUniField = () => setUniFields(prev => [...prev, { key: '', value: '' }])
  const removeUniField = (i: number) => setUniFields(prev => prev.filter((_, idx) => idx !== i))
  const updateUniField = (i: number, field: 'key' | 'value', value: string) =>
    setUniFields(prev => prev.map((f, idx) => idx === i ? { ...f, [field]: value } : f))

  return (
    <div className="space-y-6">

      {/* Search and Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Search className="h-4 w-4 inline mr-1" />
              Search Events
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search event name or category..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        </div>

        <button
          onClick={() => refetch()}
          disabled={isLoading}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium transition-colors',
            isLoading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-primary text-white hover:bg-primary/90'
          )}
        >
          {isLoading ? 'Loading...' : `Load Events${mappings.length > 0 ? ` (${mappings.length} found)` : ''}`}
        </button>

        {error && (
          <p className="mt-2 text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load events'}
          </p>
        )}
      </div>

      {/* Events Table */}
      {allMappings.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Event Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Fields</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mappings.map(m => (
                  <tr key={`${m.accId}-${m.eventType}-${m.category}-${m.eventName}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.eventName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{m.eventType}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{m.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{Object.keys(m.mappings).length} fields</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => selectMapping(m)}
                        className="bg-primary text-white px-3 py-1 rounded text-xs hover:bg-primary/90 transition-colors"
                      >
                        Use Event
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dynamic Event Form */}
      {selectedMapping && (
        <div id="event-form" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-blue-900">Selected Event</h3>
              <button onClick={() => setSelectedMapping(null)} className="text-blue-700 hover:text-blue-900">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div><span className="font-medium text-blue-900">Name:</span> {selectedMapping.eventName}</div>
              <div><span className="font-medium text-blue-900">Type:</span> {selectedMapping.eventType}</div>
              <div><span className="font-medium text-blue-900">Category:</span> {selectedMapping.category}</div>
              <div><span className="font-medium text-blue-900">Fields:</span> {Object.keys(selectedMapping.mappings).length}</div>
            </div>
          </div>

          {/* Uni Identifiers */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Universal Identifiers (Uni)</label>
            {uniFields.map((field, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={field.key}
                  onChange={e => updateUniField(i, 'key', e.target.value)}
                  placeholder="e.g., email, mobile"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <input
                  type="text"
                  value={field.value}
                  onChange={e => updateUniField(i, 'value', e.target.value)}
                  placeholder="Value"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {uniFields.length > 1 && (
                  <button
                    onClick={() => removeUniField(i)}
                    className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button onClick={addUniField} className="mt-2 text-sm text-primary hover:text-primary/80 font-medium">
              + Add Another Uni
            </button>
          </div>

          {/* Custom Fields from mapping keys */}
          {Object.keys(selectedMapping.mappings).length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Custom Attributes</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(selectedMapping.mappings).map(([intermediateField, codedField]) => (
                  <div key={intermediateField}>
                    <label className="block text-sm text-gray-600 mb-1">
                      {intermediateField}
                      <span className="ml-1 text-xs text-gray-400 font-mono">→ {codedField}</span>
                    </label>
                    <input
                      type="text"
                      value={customFields[intermediateField] || ''}
                      onChange={e => setCustomFields(prev => ({ ...prev, [intermediateField]: e.target.value }))}
                      placeholder={`Enter ${intermediateField}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optional FHID */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">FHID (Optional)</label>
            <input
              type="text"
              value={fhid}
              onChange={e => setFhid(e.target.value)}
              placeholder="Leave empty if unknown"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-gray-500 mt-1">System will resolve from uni if not provided</p>
          </div>

          {!credentials && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              API credentials not configured. Go to the <strong>Single Event API</strong> tab to set your Key ID and Secret.
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !credentials}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors',
              isSubmitting || !credentials
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
            )}
          >
            <Send className="h-5 w-5" />
            {isSubmitting ? 'Submitting...' : 'Submit Event'}
          </button>

          {submitError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-800 mt-1">{submitError}</p>
            </div>
          )}

          {response && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm font-medium text-green-900">Success!</p>
              <pre className="text-xs text-green-800 mt-2 overflow-auto">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
